import React, { useState } from "react";
import { connect } from "react-redux";
import validate from "validate.js";
import { storeUserData, updateUserInProfileSilent } from "../../redux/actions";
import {
  ContractForServices,
  HealthAndDisability,
  LegalComplianceStep,
  WorkingWeekOptOut,
} from "../../components/profile";
import history from "../../history";
import { pick } from "../../helpers";
import { submitContractForServices } from "../../api";
import RightToWorkContainer from "../RightToWorkContainer/RightToWorkContainer";

const findStep = (section, jobId = undefined) => {
  switch (section) {
    case "48-hour-opt-out":
      return `/profile/edit/legal-compliance/health-and-disability`;
    case "health-and-disability":
      return `/profile/edit/legal-compliance/contract-for-services`;
    case "contract-for-services":
      return `/profile/edit/legal-compliance/rtw`;
    default:
      return `/profile/edit/legal-compliance/48-hour-opt-out`;
  }
};

const CONSTRAINTS = {
  "health-and-disability": {
    healthAndDisabilityNote: (value, attributes) => {
      if (attributes.healthAndDisability === "yes") {
        return {
          presence: {
            allowEmpty: false,
            message:
              "^Please enter your health conditions or disabilities details.",
          },
        };
      }
      return {};
    },
  },
  "contract-for-services": {
    hasCFS: {
      presence: {
        message:
          "^Please confirm that you have read and understood the information in this contract and agree to the terms outlined in this document.",
      },
      inclusion: {
        within: [true],
        message:
          "^Please confirm that you have read and understood the information in this contract and agree to the terms outlined in this document.",
      },
    },
  },
};

const WORKING_WEEK_OPT_OUT_OPTIONS = [
  {
    value: "no",
    label: "No (I DO NOT want to work more than 48 hours per working week)",
  },
  {
    value: "yes",
    label: "Yes (I am happy to work more than 48 hours per working week)",
  },
];

const HEALTH_AND_DISABILITY_OPTIONS = [
  {
    value: "no",
    label: "No",
  },
  {
    value: "yes",
    label: "Yes",
  },
];

const ProfileLegalComplianceContainer = ({
  appData,
  section,
  updateCandidate,
  updateAppState,
  currentJobProgressState,
}) => {
  const { hasCFS, firstName, lastName } = appData;
  const candidateName = `${firstName} ${lastName}`;
  const dateSigned = new Date().toLocaleDateString();

  const useLegalComplianceState = ({
    wtr48HrWaiver = "",
    healthAndDisability = "",
    healthAndDisabilityNote = "",
  }) => {
    const optOut = wtr48HrWaiver ? "yes" : "no";
    const disability = healthAndDisability ? "yes" : "no";
    const initialState = {
      wtr48HrWaiver: optOut,
      healthAndDisability: disability,
      healthAndDisabilityNote,
      cfsSubmitted: hasCFS,
      hasCFS,
    };

    const [legalComplianceState, setLegalComplianceState] =
      useState(initialState);

    const [errors, setErrors] = useState({});

    const handleChange = (key, val) => {
      const keysToValidate = ["healthAndDisabilityNote", "hasCFS"];
      const newState = {
        ...legalComplianceState,
        [key]: val,
      };
      if (keysToValidate.includes(key) && val) {
        const validationErrors = validate(newState, CONSTRAINTS[section]);
        setErrors(validationErrors);
      }
      return setLegalComplianceState(newState);
    };

    return [legalComplianceState, handleChange, errors, setErrors];
  };

  const [state, handleChange, errors, setErrors] =
    useLegalComplianceState(appData);

  const updateCandidateData = (data) => {
    const { candidateRef, emailAddress } = appData;
    const updatedUserData = {
      candidateRef,
      emailAddress,
      ...data,
    };
    new Promise((resolve) => {
      updateCandidate(candidateRef, updatedUserData);
      resolve();
    }).then(() => {
      history.push(findStep(section));
    });
  };

  const updateOptOutData = (e) => {
    e.preventDefault();
    const updatedUserData = {
      wtr48HrWaiver: state.wtr48HrWaiver === "yes",
    };
    updateCandidateData(updatedUserData);
  };

  const updateHealthAndDisability = (fieldRefs) => {
    const validationErrors = validate(state, CONSTRAINTS[section]);

    if (validationErrors) {
      setErrors(validationErrors);
      const firstInvalidField = Object.keys(validationErrors)[0];
      fieldRefs.current[firstInvalidField]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    } else {
      const hasHealthAndDisability = state.healthAndDisability === "yes";
      const updatedUserData = {
        healthAndDisability: hasHealthAndDisability,
        healthAndDisabilityNote: hasHealthAndDisability
          ? state.healthAndDisabilityNote
          : null,
      };
      updateCandidateData(updatedUserData);
    }
  };

  const signContractForServices = (e) => {
    e.preventDefault();
    const validationErrors = validate(state, CONSTRAINTS[section]);

    if (validationErrors) {
      return setErrors(validationErrors);
    }
    if (!state.cfsSubmitted) {
      handleChange("cfsSubmitted", true);
      updateAppState({ ...appData, hasCFS: true });
      submitContractForServices().then((r) => () => {});
    }

    return history.push(findStep(section));
  };

  const findNextStepAfterRTW = () => {
    if (section === "error") {
      return "/profile/edit/legal-compliance/rtw";
    }
    return `/profile`;
  };

  const commonRTWStep = {
    component: (
      <RightToWorkContainer
        findStep={findStep}
        nextStepWhenCompleted={() => findNextStepAfterRTW(section)}
        section={section}
      />
    ),
    title: "Right to work in the UK",
    description:
      "Submit your documents as evidence of your right to work in the UK",
    imagePath: "/images/rtw.svg",
    imageClass: "rtw__image",
    currentStep: 4,
  };

  const steps = {
    "48-hour-opt-out": {
      component: (
        <WorkingWeekOptOut
          handleSubmit={updateOptOutData}
          handleBack={() =>
            history.push(`/profile/edit/registration/demographics`)
          }
          state={state}
          handleChange={handleChange}
          options={WORKING_WEEK_OPT_OUT_OPTIONS}
        />
      ),
      title: "48 hour working week opt in/opt out",
      description:
        "If you are over the age of 18, you have the option to opt out of the 48 hour per week working limit.",
      imagePath: "/images/48_hour_opt_out.svg",
      imageClass: "optOut__image",
      currentStep: 1,
    },
    "health-and-disability": {
      component: (
        <HealthAndDisability
          handleSubmit={updateHealthAndDisability}
          handleBack={() => history.push(findStep("initial"))}
          errors={errors}
          state={state}
          handleChange={handleChange}
          options={HEALTH_AND_DISABILITY_OPTIONS}
        />
      ),
      title: "Health and disability question",
      description:
        "Please let us know if you have any health or disability issues",
      imagePath: "/images/health_and_disability.svg",
      imageClass: "healthAndDisability__image",
      currentStep: 2,
    },
    "contract-for-services": {
      component: (
        <ContractForServices
          handleSubmit={signContractForServices}
          handleBack={() => history.push(findStep("48-hour-opt-out"))}
          state={state}
          errors={errors}
          handleChange={handleChange}
          candidateName={candidateName}
          dateSigned={dateSigned}
        />
      ),
      title: "Contract for services",
      description:
        "Please read the HRGO contract for services below and agree to the terms in the document",
      imagePath: "/images/contract_for_services.svg",
      imageClass: "contract__image",
      currentStep: 3,
    },
    rtw: commonRTWStep,
    success: commonRTWStep,
    error: commonRTWStep,
  };

  const currentStep = steps[section] || {};
  //  -2 because of success and error keys for YOTI callback
  return (
    <LegalComplianceStep
      totalSteps={Object.keys(steps).length - 2}
      currentJobProgressState={currentJobProgressState}
      {...currentStep}
    />
  );
};

const mapStateToProps = (state) => {
  const appData = state.core.app || {};
  const legalComplianceItems = pick(
    appData,
    ["healthAndDisability", "healthAndDisabilityNote", "wtr48HrWaiver"],
    null
  );
  return { appData, ...legalComplianceItems };
};

const mapDispatchToProps = {
  updateCandidate: updateUserInProfileSilent,
  updateAppState: storeUserData,
};

const withRedux = connect(mapStateToProps, mapDispatchToProps);

export default withRedux(ProfileLegalComplianceContainer);
